import {
  collection,
  collectionData,
  CollectionReference,
  doc,
  docData,
  DocumentReference,
  Firestore,
  Timestamp,
} from "@angular/fire/firestore"
import { computed, inject, Injectable } from "@angular/core"
import { ProfileService } from "../user-profile/profile.service"
import { Profile } from "../user-profile/profile.model"
import { toObservable, toSignal } from "@angular/core/rxjs-interop"
import { of, switchMap } from "rxjs"
import { decodeCustomClaims } from "redlineLibs"

type UserAccount = { profile: Profile }
type UserAuthCustomClaimsDoc = {
  users: {
    [userId: string]: {
      roles: string[],
      regionRoles: string[],
    }
  }
  lastUpdated: Timestamp,
}

@Injectable({
  providedIn: "root",
})
export class UserManagerService {
  private firestore = inject(Firestore)
  private profileService = inject(ProfileService)

  isUserAdmin = this.profileService.isUserAdmin

  private userAccountsCollection = collection(
    this.firestore,
    "user-accounts",
  ) as CollectionReference<UserAccount>

  private userAuthCustomClaimsDocRef = doc(
    this.firestore,
    "aggregates/user-auth-custom-claims",
  ) as DocumentReference<UserAuthCustomClaimsDoc>

  /**
   * Stream user-accounts only while the current user is a user-admin.
   * When isUserAdmin() flips to false the inner observable is swapped
   * for `of([])`, which automatically unsubscribes the Firestore listener.
   */
  private userAccounts = toSignal(
    toObservable(this.isUserAdmin).pipe(
      switchMap(isUserAdmin => isUserAdmin
        ? collectionData(this.userAccountsCollection)
        : of<UserAccount[]>([]),
      ),
    ),
    { initialValue: [] as UserAccount[] },
  )

  userProfiles_map = computed(() =>
    new Map(this.userAccounts()
      .map(a =>
        [a.profile.userId, a.profile])),
  )

  userAuthCustomClaims = computed(() =>
    Object.fromEntries(
      Object.entries(this.userAuthCustomClaimsOoc()?.users || {})
        .map(([userId, customClaimsEncoded]) =>
          [userId, decodeCustomClaims(customClaimsEncoded)],
        ),
    ),
  )

  private userAuthCustomClaimsOoc = toSignal(
    toObservable(this.isUserAdmin).pipe(
      switchMap(isUserAdmin => isUserAdmin
        ? docData(this.userAuthCustomClaimsDocRef)
        : of<UserAuthCustomClaimsDoc>({} as UserAuthCustomClaimsDoc),
      ),
    ),
    { initialValue: {} as UserAuthCustomClaimsDoc },
  )

}
